// ============================================================
// VidyaTrack — Sidebar (fully mobile-ready)
// Uses windowWidth to control transform via inline style
// so it isn't blocked by CSS specificity issues.
// ============================================================

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  FiHome, FiBook, FiUsers, FiCheckSquare, FiBell,
  FiFileText, FiGrid, FiCalendar, FiBarChart2,
  FiLogOut, FiSettings, FiUpload, FiUser,
} from "react-icons/fi";
import { MdOutlineClass } from "react-icons/md";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { toast } from "react-toastify";

// ── Nav items per role ────────────────────────────────────────
const NAV = {
  admin: [
    { path:"/dashboard",        label:"Dashboard",       icon:FiHome },
    { path:"/admin/students",   label:"Students",        icon:FiUsers },
    { path:"/admin/teachers",   label:"Teachers",        icon:FiUser },
    { path:"/admin/parents",    label:"Parents",         icon:FiUsers },
    { path:"/admin/classes",    label:"Classes",         icon:MdOutlineClass },
    { path:"/admin/attendance", label:"Attendance",      icon:FiCheckSquare },
    { path:"/admin/homework",   label:"Homework",        icon:FiBook },
    { path:"/admin/notices",    label:"Notices",         icon:FiBell },
    { path:"/admin/calendar",   label:"Calendar",        icon:FiCalendar },
    { path:"/admin/reports",    label:"Reports",         icon:FiBarChart2 },
  ],
  teacher: [
    { path:"/dashboard",          label:"Dashboard",      icon:FiHome },
    { path:"/teacher/homework",   label:"Homework",       icon:FiBook },
    { path:"/teacher/attendance", label:"Attendance",     icon:FiCheckSquare },
    { path:"/teacher/materials",  label:"Study Materials",icon:FiUpload },
    { path:"/teacher/groups",     label:"Groups",         icon:FiGrid },
    { path:"/teacher/notices",    label:"Notices",        icon:FiBell },
    { path:"/teacher/calendar",   label:"Calendar",       icon:FiCalendar },
    { path:"/teacher/reports",    label:"Reports",        icon:FiBarChart2 },
  ],
  student: [
    { path:"/dashboard",          label:"Dashboard",      icon:FiHome },
    { path:"/student/homework",   label:"My Homework",    icon:FiBook },
    { path:"/student/attendance", label:"My Attendance",  icon:FiCheckSquare },
    { path:"/student/materials",  label:"Study Materials",icon:FiFileText },
    { path:"/student/groups",     label:"My Groups",      icon:FiGrid },
    { path:"/student/notices",    label:"Notices",        icon:FiBell },
    { path:"/student/calendar",   label:"Calendar",       icon:FiCalendar },
  ],
  parent: [
    { path:"/dashboard",          label:"Dashboard",          icon:FiHome },
    { path:"/parent/homework",    label:"Child's Homework",   icon:FiBook },
    { path:"/parent/attendance",  label:"Child's Attendance", icon:FiCheckSquare },
    { path:"/parent/notices",     label:"School Notices",     icon:FiBell },
    { path:"/parent/calendar",    label:"Calendar",           icon:FiCalendar },
  ],
};

const ROLE_STYLE = {
  admin  : { grad:"linear-gradient(135deg,#054bb4,#2e5caf)", label:"Administrator", bg:"#e8eef8" },
  teacher: { grad:"linear-gradient(135deg,#059669,#10b981)", label:"Teacher",       bg:"#ecfdf5" },
  student: { grad:"linear-gradient(135deg,#7c3aed,#a78bfa)", label:"Student",       bg:"#f5f3ff" },
  parent : { grad:"linear-gradient(135deg,#d97706,#f59e0b)", label:"Parent",        bg:"#fffbeb" },
};

const SIDEBAR_W = 260;

export default function Sidebar({ mobileOpen, setMobileOpen }) {
  const { userData, logout } = useAuth();
  const { dark }             = useTheme();
  const navigate             = useNavigate();
  const location             = useLocation();

  // Track viewport width in state so sidebar re-renders on resize
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  const role     = userData?.role || "student";
  const navItems = NAV[role] || [];
  const rs       = ROLE_STYLE[role] || ROLE_STYLE.student;
  const initials = (userData?.name || "VT").split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);

  const go = (path) => { navigate(path); setMobileOpen(false); };

  const handleLogout = async () => {
    try { await logout(); navigate("/login"); toast.success("Signed out successfully"); }
    catch { toast.error("Logout failed"); }
  };

  // ── Colours ──────────────────────────────────────────────
  const bg      = dark ? "#1a2535" : "#ffffff";
  const border  = dark ? "#2d3748" : "#f0f0f0";
  const textClr = dark ? "#cbd5e1" : "#374151";
  const subClr  = dark ? "#64748b" : "#9ca3af";
  const actBg   = dark ? "#1e3a6e" : "#e8eef8";
  const actClr  = dark ? "#93c5fd" : "#054bb4";
  const hovBg   = dark ? "#1e293b" : "#f9fafb";

  // ── Sidebar position: fixed on desktop, slide-in on mobile ──
  const sidebarStyle = {
    width:           SIDEBAR_W,
    background:      bg,
    borderRight:     `1px solid ${border}`,
    height:          "100vh",
    position:        "fixed",
    left:            0,
    top:             0,
    display:         "flex",
    flexDirection:   "column",
    zIndex:          1000,
    overflowY:       "auto",
    overflowX:       "hidden",
    // KEY FIX: control transform via JS, not CSS class,
    // so inline style always wins regardless of specificity
    transform:       isMobile
      ? (mobileOpen ? "translateX(0)" : "translateX(-100%)")
      : "translateX(0)",
    transition:      "transform 0.28s cubic-bezier(0.4,0,0.2,1)",
    willChange:      "transform",
    boxShadow:       mobileOpen && isMobile
      ? "4px 0 24px rgba(0,0,0,0.18)"
      : "1px 0 0 " + border,
  };

  return (
    <>
      {/* Backdrop — only on mobile when sidebar is open */}
      {isMobile && mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position:   "fixed",
            inset:      0,
            background: "rgba(0,0,0,0.45)",
            zIndex:     999,
            cursor:     "pointer",
          }}
          aria-label="Close menu"
        />
      )}

      <aside style={sidebarStyle}>

        {/* ── Logo ── */}
        <div style={{ padding:"16px 18px", borderBottom:`1px solid ${border}`, display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
          <Link to="/" style={{ display:"flex", alignItems:"center", gap:10, textDecoration:"none" }}>
            <img src={dark ? "/vtDark.png" : "/vtLight.png"} alt="VidyaTrack" style={{ width:38, height:38, objectFit:"contain", flexShrink:0 }} />
            <div>
              <div style={{ fontFamily:"'Poppins',sans-serif", fontWeight:800, fontSize:".95rem", color:"#054bb4", lineHeight:1 }}>VidyaTrack</div>
              <div style={{ fontSize:".58rem", color:subClr, marginTop:1 }}>School Management</div>
            </div>
          </Link>
          {/* Close button on mobile */}
          {isMobile && (
            <button
              onClick={() => setMobileOpen(false)}
              style={{ background:"none", border:"none", cursor:"pointer", color:subClr, padding:4, borderRadius:6, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.2rem" }}
              aria-label="Close sidebar"
            >✕</button>
          )}
        </div>

        {/* ── User card ── */}
        <div style={{ margin:"12px 12px 8px", background:rs.bg, borderRadius:10, padding:"11px 13px", display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
          <div style={{ width:36, height:36, borderRadius:"50%", background:rs.grad, color:"#fff", fontWeight:800, fontSize:".82rem", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            {initials}
          </div>
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:".84rem", fontWeight:700, color:dark?"#f1f5f9":"#111827", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              {userData?.name || "User"}
            </div>
            <div style={{ fontSize:".62rem", fontWeight:700, marginTop:3, background:rs.grad, color:"#fff", padding:"1px 8px", borderRadius:20, display:"inline-block", textTransform:"uppercase", letterSpacing:".03em" }}>
              {rs.label}
            </div>
          </div>
        </div>

        {/* ── Nav items ── */}
        <nav style={{ flex:1, padding:"4px 10px 8px", overflowY:"auto" }}>
          <div style={{ fontSize:".62rem", fontWeight:700, color:subClr, textTransform:"uppercase", letterSpacing:".9px", marginBottom:4, padding:"0 8px" }}>
            Menu
          </div>
          {navItems.map(item => {
            const active = location.pathname === item.path
              || (item.path !== "/dashboard" && location.pathname.startsWith(item.path));
            const Icon = item.icon;
            return (
              <button
                key={item.path}
                onClick={() => go(item.path)}
                style={{
                  width:"100%",
                  display:"flex", alignItems:"center", gap:10,
                  padding:"9px 10px",
                  borderRadius:8,
                  border:"none",
                  borderLeft: active ? `2px solid ${actClr}` : "2px solid transparent",
                  background: active ? actBg : "transparent",
                  color: active ? actClr : textClr,
                  fontWeight: active ? 700 : 400,
                  fontSize:".84rem",
                  cursor:"pointer",
                  marginBottom:2,
                  transition:"background .15s, color .15s",
                  textAlign:"left",
                  fontFamily:"Inter,sans-serif",
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = hovBg; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = "transparent"; }}
              >
                <Icon size={15} style={{ color: active ? actClr : subClr, flexShrink:0 }}/>
                <span style={{ flex:1 }}>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* ── Bottom actions ── */}
        <div style={{ padding:"8px 10px", borderTop:`1px solid ${border}`, flexShrink:0 }}>
          <button
            onClick={() => go("/profile")}
            style={{ width:"100%", display:"flex", alignItems:"center", gap:10, padding:"9px 10px", borderRadius:8, border:"none", background:"transparent", color:textClr, cursor:"pointer", fontSize:".84rem", fontFamily:"Inter,sans-serif", marginBottom:2, transition:"background .15s" }}
            onMouseEnter={e => e.currentTarget.style.background = hovBg}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
          >
            <FiSettings size={14} style={{ color:subClr }}/> Settings
          </button>
          <button
            onClick={handleLogout}
            style={{ width:"100%", display:"flex", alignItems:"center", gap:10, padding:"9px 10px", borderRadius:8, border:"none", background:"transparent", color:"#ef4444", cursor:"pointer", fontSize:".84rem", fontFamily:"Inter,sans-serif", fontWeight:600, transition:"background .15s" }}
            onMouseEnter={e => e.currentTarget.style.background = dark ? "#3d1515" : "#fef2f2"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
          >
            <FiLogOut size={14}/> Sign Out
          </button>
        </div>
      </aside>
    </>
  );
}
